import React, { useEffect, useState, useRef } from "react";
import SignatureCanvas from "react-signature-canvas";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { addLogoAndFormHeader, applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import { canEditDeleteRecords } from "../../utils/rolePermissions";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import {
  SF009_SIG_H,
  SF009_SIG_W,
  getPdfImageType,
  hydrateSigPad,
  isSigImage,
  toSigDataUrl,
} from "./sf009FormSignatureUtils";

const K_PDF_SIG_MAX_W = 32;
const K_PDF_SIG_MAX_H = 10;
const K_PDF_ASSESSOR_ROW_H = 14;

const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;
const ASSESSOR_ROW_COUNT = 2;
const HAZARD_ROW_COUNT = 6;
const PERSONS_CONDUCTING_ASSESSMENT_KEY = "Persons conducting assessment";
const RATING_OPTIONS = [1, 2, 3, 4, 5];

const LIKELIHOOD_LEGEND = [
  "Rating 1 = Very unlikely",
  "Rating 2 = Unlikely",
  "Rating 3 = Likely",
  "Rating 4 = Very likely",
  "Rating 5 = Almost certain",
];

const SEVERITY_LEGEND = [
  "Rating 1 = No injury",
  "Rating 2 = Minor injury or illness",
  'Rating 3 = "7 day" injury or illness',
  "Rating 4 = Major injury or illness",
  "Rating 5 = Fatality, disabling injury, etc.",
];

const HIERARCHY_ITEMS = [
  "a) Elimination",
  "b) Substitution",
  "c) Engineering Controls",
  "d) Signage / Warnings and / or Administrative Controls",
  "e) PPE as a last resort",
];

const FACTORS_ITEMS = [
  "H – History – is the method well used or new technology",
  "A – Activity",
  "P – People / their skills & experience",
  "E – Equipment / Tools",
  "M – Materials and associated health hazards",
  "E – Environmental factors / environment of the work",
  "E – Energised systems / stored energy",
  "T – Time of activity / shift handovers / limited time windows to do work",
];

function riskScore(likelihood, severity) {
  const l = parseInt(likelihood, 10);
  const s = parseInt(severity, 10);
  if (!l || !s || isNaN(l) || isNaN(s)) return "";
  return String(l * s);
}

function riskCellColor(score) {
  const n = parseInt(score, 10);
  if (!n || isNaN(n)) return "#fff";
  if (n <= 4) return "#c6efce";
  if (n <= 12) return "#ffeb9c";
  return "#ffc7ce";
}

function buildRiskMatrix() {
  const rows = [];
  for (let severity = 5; severity >= 1; severity--) {
    const row = { severity, cells: [] };
    for (let likelihood = 1; likelihood <= 5; likelihood++) {
      row.cells.push({ likelihood, severity, score: severity * likelihood });
    }
    rows.push(row);
  }
  return rows;
}

const RISK_MATRIX = buildRiskMatrix();

function emptyAssessorRow() {
  return { name: "", signature: "", date: "" };
}

function emptyHazardRow() {
  return {
    hazard: "",
    initialLikelihood: "",
    initialSeverity: "",
    initialRisk: "",
    controlMeasures: "",
    residualLikelihood: "",
    residualSeverity: "",
    residualRisk: "",
    implementedBy: "",
    controlFrequencyCheckRate: "",
  };
}

function emptySupervisorDetails() {
  return { name: "", signature: "", date: "" };
}

function scalarFormField(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value).trim();
  if (typeof value === "object" && !Array.isArray(value)) {
    const vals = Object.values(value).filter((v) => v != null && String(v).trim() !== "");
    if (vals.length) return String(vals[0]).trim();
  }
  return "";
}

function normalizeSupervisorDetails(src, thirdRowFromAssessors = null) {
  const block =
    src?.supervisorDetails && typeof src.supervisorDetails === "object"
      ? src.supervisorDetails
      : {};
  const third =
    thirdRowFromAssessors && typeof thirdRowFromAssessors === "object" ? thirdRowFromAssessors : {};
  let sig =
    block.signature ??
    src.supervisorSignature ??
    src["Supervisor Signature"] ??
    src["Supervisor Signature:"] ??
    src.supervisor_signature ??
    third.signature ??
    "";
  if (isSigImage(sig)) {
    const s = String(sig).trim();
    sig = s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
  } else {
    sig = "";
  }
  return {
    name:
      block.name ??
      src.supervisorName ??
      src["Supervisor Name"] ??
      src["Supervisor Name:"] ??
      third.name ??
      "",
    signature: sig,
    date:
      block.date ??
      src.supervisorDate ??
      src["Supervisor Date"] ??
      src["Supervisor Date:"] ??
      third.date ??
      "",
  };
}

const DEFAULT_FORM_DATA = {
  documentClassification: "Public",
  site: "",
  activity: "",
  sapNo: "",
  riskAssessmentNo: "",
  reviewDate: "",
  revisionNo: "",
  personSupervisingWork: "",
  employer: "",
  supervisorDetails: emptySupervisorDetails(),
  [PERSONS_CONDUCTING_ASSESSMENT_KEY]: [],
  personsExposed: {
    employees: "",
    otherWorkers: "",
    publicVisitors: "",
    vulnerablePersons: "",
    totalAtRisk: "",
  },
  hazardEntries: [],
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function normalizeAssessorRow(src) {
  const row = src && typeof src === "object" ? src : {};
  let sig = row.signature ?? "";
  if (isSigImage(sig)) {
    const s = String(sig).trim();
    sig = s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
  } else {
    sig = "";
  }
  return {
    name: row.name ?? "",
    signature: sig,
    date: row.date ?? "",
  };
}

function normalizePersonsExposed(src) {
  const pe = src && typeof src === "object" ? src : {};
  return {
    employees: pe.employees ?? "",
    otherWorkers: pe.otherWorkers ?? pe.other_workers ?? "",
    publicVisitors: pe.publicVisitors ?? pe.public_visitors ?? "",
    vulnerablePersons: pe.vulnerablePersons ?? pe.vulnerable_persons ?? "",
    totalAtRisk: pe.totalAtRisk ?? pe.total_at_risk ?? "",
  };
}

function normalizeHazardRow(src) {
  const row = src && typeof src === "object" ? src : {};
  const il = row.initialLikelihood ?? row.initial_likelihood ?? "";
  const is = row.initialSeverity ?? row.initial_severity ?? "";
  const rl = row.residualLikelihood ?? row.residual_likelihood ?? "";
  const rs = row.residualSeverity ?? row.residual_severity ?? "";
  return {
    hazard: row.hazard ?? "",
    initialLikelihood: il,
    initialSeverity: is,
    initialRisk: row.initialRisk ?? row.initial_risk ?? riskScore(il, is),
    controlMeasures: row.controlMeasures ?? row.control_measures ?? "",
    residualLikelihood: rl,
    residualSeverity: rs,
    residualRisk: row.residualRisk ?? row.residual_risk ?? riskScore(rl, rs),
    implementedBy: row.implementedBy ?? row.implemented_by ?? "",
    controlFrequencyCheckRate:
      row.controlFrequencyCheckRate ?? row.control_frequency_check_rate ?? row.checkRate ?? "",
  };
}

function hasHazardRowData(row) {
  if (!row || typeof row !== "object") return false;
  return Object.values(row).some((v) => v != null && String(v).trim() !== "");
}

function hasAssessorRowData(row) {
  if (!row || typeof row !== "object") return false;
  return [row.name, row.signature, row.date].some((v) => v != null && String(v).trim() !== "");
}

function parseAssessorList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function getPersonsConductingAssessment(src) {
  const base = src && typeof src === "object" ? src : {};
  return (
    base[PERSONS_CONDUCTING_ASSESSMENT_KEY] ??
    base.personsConductingAssessment ??
    base.assessors ??
    base.persons_conducting_assessment
  );
}

function withPersonsConductingAssessment(obj, rows) {
  const next = { ...(obj && typeof obj === "object" ? obj : {}) };
  next[PERSONS_CONDUCTING_ASSESSMENT_KEY] = rows;
  delete next.personsConductingAssessment;
  return next;
}

function assessorsFromRecord(src) {
  const base = src && typeof src === "object" ? src : {};
  const parsed = parseAssessorList(getPersonsConductingAssessment(base));
  const fromArray = parsed.length >= 3 ? parsed.slice(0, 2) : parsed;
  const rows = [];
  for (let i = 0; i < ASSESSOR_ROW_COUNT; i += 1) {
    const fromArr = fromArray[i] || {};
    const n = i + 1;
    rows.push(
      normalizeAssessorRow({
        name: fromArr.name ?? base[`assessor${n}Name`],
        signature: fromArr.signature ?? base[`assessor${n}Signature`],
        date: fromArr.date ?? base[`assessor${n}Date`],
      })
    );
  }
  return rows;
}

function thirdAssessorSupervisorRow(src) {
  const base = src && typeof src === "object" ? src : {};
  const parsed = parseAssessorList(getPersonsConductingAssessment(base));
  if (parsed.length < 3) return null;
  return normalizeAssessorRow(parsed[2]);
}

function buildSavePayload(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const thirdRow = thirdAssessorSupervisorRow(src);
  const assessors = assessorsForEdit(assessorsFromRecord(src)).filter(hasAssessorRowData);
  const supervisorDetails = normalizeSupervisorDetails(src, thirdRow);
  return {
    documentClassification: src.documentClassification || "Public",
    site: scalarFormField(src.site ?? src["Site:"]) || "",
    activity: scalarFormField(src.activity ?? src.Activity) || "",
    sapNo: scalarFormField(src.sapNo ?? src["SAP No:"]) || "",
    riskAssessmentNo:
      scalarFormField(src.riskAssessmentNo ?? src["Risk Assessment No."] ?? src["Risk Assessment No"]) || "",
    reviewDate: scalarFormField(src.reviewDate ?? src["Review Date"]) || "",
    revisionNo: scalarFormField(src.revisionNo ?? src["Revision No."] ?? src["Revision No"]) || "",
    personSupervisingWork: scalarFormField(src.personSupervisingWork ?? src["Person Supervising Work"]) || "",
    employer: scalarFormField(src.employer ?? src.Employer) || "",
    personsExposed: normalizePersonsExposed(
      src.personsExposed ?? {
        employees: src.Employees,
        otherWorkers: src["Other Workers"],
        publicVisitors: src["Public/Visitors"],
        vulnerablePersons: src["Vulnerable Persons"],
        totalAtRisk: src["Total at Risk"],
      }
    ),
    [PERSONS_CONDUCTING_ASSESSMENT_KEY]: assessors,
    supervisorDetails,
    hazardEntries: hazardEntriesFromRaw(src.hazardEntries),
    supervisorName: supervisorDetails.name || "",
    supervisorSignature: supervisorDetails.signature || "",
    supervisorDate: supervisorDetails.date || "",
  };
}

function assessorsFromRaw(list) {
  const arr = parseAssessorList(list).map(normalizeAssessorRow);
  return arr.filter(hasAssessorRowData);
}

function assessorsForEdit(list) {
  const filled = Array.isArray(list) ? list.map(normalizeAssessorRow) : [];
  const rows = [...filled];
  while (rows.length < ASSESSOR_ROW_COUNT) rows.push(emptyAssessorRow());
  return rows;
}

function hazardEntriesFromRaw(entries) {
  const list = Array.isArray(entries) ? entries.map(normalizeHazardRow) : [];
  return list.filter(hasHazardRowData);
}

function cloneHazardEntries(entries) {
  return Array.isArray(entries) ? entries.map(normalizeHazardRow) : [];
}

function hazardsForPdf(entries) {
  const filled = hazardEntriesFromRaw(entries);
  const rows = [...filled];
  while (rows.length < HAZARD_ROW_COUNT) rows.push(emptyHazardRow());
  return rows;
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  return {
    documentClassification:
      src.documentClassification ?? hd.documentClassification ?? hd.document_classification ?? "Public",
    site: scalarFormField(src.site ?? src["Site:"] ?? hd.site) || "",
    activity: scalarFormField(src.activity ?? src.Activity ?? hd.activity) || "",
    sapNo: scalarFormField(src.sapNo ?? src["SAP No:"] ?? hd.sapNo ?? hd.sap_no) || "",
    riskAssessmentNo:
      scalarFormField(
        src.riskAssessmentNo ?? src["Risk Assessment No."] ?? src["Risk Assessment No"] ?? hd.riskAssessmentNo
      ) || "",
    reviewDate: scalarFormField(src.reviewDate ?? src["Review Date"] ?? hd.reviewDate ?? hd.review_date) || "",
    revisionNo: scalarFormField(src.revisionNo ?? src["Revision No."] ?? src["Revision No"] ?? hd.revisionNo) || "",
    personSupervisingWork:
      scalarFormField(src.personSupervisingWork ?? src["Person Supervising Work"] ?? hd.personSupervisingWork) || "",
    employer: scalarFormField(src.employer ?? src.Employer ?? hd.employer) || "",
    supervisorDetails: normalizeSupervisorDetails(src, thirdAssessorSupervisorRow(src)),
    [PERSONS_CONDUCTING_ASSESSMENT_KEY]: assessorsFromRecord(src),
    personsExposed: normalizePersonsExposed(
      src.personsExposed ??
        src.persons_exposed ?? {
          employees: src.Employees,
          otherWorkers: src["Other Workers"],
          publicVisitors: src["Public/Visitors"],
          vulnerablePersons: src["Vulnerable Persons"],
          totalAtRisk: src["Total at Risk"],
        }
    ),
    hazardEntries: hazardEntriesFromRaw(
      src.hazardEntries ?? src.riskAssessmentEntries ?? src.hazards ?? src.entries
    ),
  };
}

function dateToLocalYMD(d) {
  if (!d || !(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDateForPicker(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const slashOrDash = parseSiteRegisterDate(value);
  if (slashOrDash) return slashOrDash;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 11,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 11,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegHeadCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 8,
  fontWeight: 600,
  background: "rgb(239, 226, 231)",
  color: THEME,
  textAlign: "center",
  verticalAlign: "middle",
};
const sf009RegCell = {
  border: sf009TblBorder,
  padding: "3px 4px",
  fontSize: 10,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 10,
  padding: "3px 2px",
  background: "transparent",
  boxSizing: "border-box",
};
const sf009StaticCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 10,
  background: "#fff",
  verticalAlign: "top",
  lineHeight: 1.4,
};

function AssessorSignatureDisplay({ value }) {
  const url = isSigImage(value) ? toSigDataUrl(value) : "";
  if (!url) return <span style={{ color: "#a0aec0", fontSize: 9 }}>—</span>;
  return (
    <img
      src={url}
      alt="Signature"
      style={{ maxWidth: "100%", maxHeight: 56, objectFit: "contain", display: "block", margin: "0 auto" }}
    />
  );
}

function AssessorSignatureField({ signature, sigRef, onSignatureChange, fieldKey = "" }) {
  const [redraw, setRedraw] = useState(false);
  const hasStored = isSigImage(signature) && !redraw;

  useEffect(() => {
    setRedraw(false);
  }, [fieldKey, signature]);

  useEffect(() => {
    if (hasStored || !sigRef?.current) return;
    const t = window.setTimeout(() => hydrateSigPad(sigRef, signature), 80);
    return () => window.clearTimeout(t);
  }, [hasStored, signature, sigRef, fieldKey]);

  if (hasStored) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
        <AssessorSignatureDisplay value={signature} />
        <button
          type="button"
          onClick={() => {
            setRedraw(true);
            onSignatureChange("");
            sigRef?.current?.clear();
          }}
          style={{
            fontSize: 9,
            color: THEME,
            background: "none",
            border: "none",
            cursor: "pointer",
            textDecoration: "underline",
            padding: 0,
          }}
        >
          Redraw
        </button>
      </div>
    );
  }

  return (
    <div style={{ border: `1px solid ${THEME_BORDER}`, borderRadius: 4, background: "#fafafa" }}>
      <SignatureCanvas
        ref={sigRef}
        penColor="#000"
        canvasProps={{ width: SF009_SIG_W, height: SF009_SIG_H, className: "sigCanvas w-100" }}
        onEnd={() => {
          if (sigRef?.current && !sigRef.current.isEmpty()) {
            onSignatureChange(sigRef.current.getCanvas().toDataURL("image/png"));
            setRedraw(false);
          }
        }}
      />
    </div>
  );
}

function Sf009KRiskAssessmentTemplatePage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [assessorSignatureLock, setAssessorSignatureLock] = useState([]);
  const [supervisorSignatureLock, setSupervisorSignatureLock] = useState("");


  const assessorSigRefs = [useRef(null), useRef(null)];
  const supervisorSigRef = useRef(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = (f) => {
    if (!f || typeof f !== "object") return {};
    if (f.formData && typeof f.formData === "object") return f.formData;
    const { formData, userId, createdBy, _id, __v, createdAt, updatedAt, ...rest } = f;
    return rest;
  };

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-k-risk-assessment-template", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.site, fd.activity, fd.sapNo, fd.riskAssessmentNo, f.name].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section === "personsExposed") {
        next.formData.personsExposed = { ...(next.formData.personsExposed || {}), [key]: value };
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      next.name = next.formData.site || next.formData.riskAssessmentNo || "";
      next.date = next.formData.reviewDate || "";
      return next;
    });
  };

  const updateAssessorRow = (idx, field, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const rows = assessorsForEdit(getPersonsConductingAssessment(next.formData));
      rows[idx] = { ...rows[idx], [field]: value };
      next.formData = withPersonsConductingAssessment(next.formData, rows);
      return next;
    });
    if (field === "signature") {
      setAssessorSignatureLock((prev) => {
        const next = [...prev];
        while (next.length < ASSESSOR_ROW_COUNT) next.push("");
        next[idx] = value || "";
        return next;
      });
    }
  };

  const updateSupervisorDetails = (field, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      next.formData.supervisorDetails = {
        ...normalizeSupervisorDetails(next.formData),
        [field]: value,
      };
      return next;
    });
    if (field === "signature") {
      setSupervisorSignatureLock(value || "");
    }
  };

  const updateHazardRow = (idx, field, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneHazardEntries(next.formData.hazardEntries);
      const row = { ...entries[idx], [field]: value };
      if (field === "initialLikelihood" || field === "initialSeverity") {
        row.initialRisk = riskScore(row.initialLikelihood, row.initialSeverity);
      }
      if (field === "residualLikelihood" || field === "residualSeverity") {
        row.residualRisk = riskScore(row.residualLikelihood, row.residualSeverity);
      }
      entries[idx] = row;
      next.formData.hazardEntries = entries;
      return next;
    });
  };

  const applyLockedAssessorSignatures = (assessors, lock) => {
    return assessors.map((row, i) => ({
      ...row,
      signature: lock[i] || row.signature,
    }));
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    const merged = mergeRecordIntoDefaults(raw);
    const editAssessors = assessorsForEdit(getPersonsConductingAssessment(merged));
    const supervisor = normalizeSupervisorDetails(merged, thirdAssessorSupervisorRow(raw));
    setAssessorSignatureLock(editAssessors.map((r) => r.signature || ""));
    setSupervisorSignatureLock(supervisor.signature || "");
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: withPersonsConductingAssessment(
        {
          ...merged,
          supervisorDetails: supervisor,
        },
        editAssessors
      ),
      name: merged.site || merged.riskAssessmentNo || "",
      date: merged.reviewDate || "",
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    const raw = { ...(editForm.formData || {}) };
    const assessorRows = assessorsForEdit(getPersonsConductingAssessment(raw));
    assessorSigRefs.forEach((ref, i) => {
      if (ref?.current && !ref.current.isEmpty()) {
        assessorRows[i] = {
          ...assessorRows[i],
          signature: ref.current.getCanvas().toDataURL("image/png"),
        };
      }
    });
    const assessorsWithSigs = applyLockedAssessorSignatures(assessorRows, assessorSignatureLock);
    const supervisorDetails = normalizeSupervisorDetails(
      withPersonsConductingAssessment(raw, assessorsWithSigs),
      thirdAssessorSupervisorRow(raw)
    );
    if (supervisorSigRef?.current && !supervisorSigRef.current.isEmpty()) {
      supervisorDetails.signature = supervisorSigRef.current.getCanvas().toDataURL("image/png");
    } else if (supervisorSignatureLock) {
      supervisorDetails.signature = supervisorSignatureLock;
    }
    const payload = buildSavePayload(withPersonsConductingAssessment({ ...raw, supervisorDetails }, assessorsWithSigs));
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-k-risk-assessment-template/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...payload,
          name: editForm.name || payload.site || payload.riskAssessmentNo,
          date: editForm.date || payload.reviewDate,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        setAssessorSignatureLock([]);
        setSupervisorSignatureLock("");
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-k-risk-assessment-template/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseDateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (r) => {
    const uid = r.createdBy;
    if (!uid) return "—";
    const u = appUsers.find((x) => String(x._id) === String(uid));
    if (u) {
      const full = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
      return full || u.email || uid;
    }
    return uid || "—";
  };

  const safeDate = (v) => parseDateForPicker(v);
  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const raw = mergeRecordIntoDefaults(getRawFormData(record));
    const assessors = assessorsForEdit(assessorsFromRecord(raw));
    const supervisor = normalizeSupervisorDetails(raw);
    const pe = raw.personsExposed || {};
    const hazards = hazardsForPdf(raw.hazardEntries);

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const pendingSignaturePromises = [];

    const queueKPdfSignature = (signatureData, data) => {
      if (!isSigImage(signatureData) || data.section !== "body" || data.column.index !== 5) return;
      const src = toSigDataUrl(signatureData);
      const pad = 1.5;
      const boxW = Math.max(8, (data.cell.width || K_PDF_SIG_MAX_W) - pad * 2);
      const boxH = Math.max(6, (data.cell.height || K_PDF_ASSESSOR_ROW_H) - pad * 2);
      const maxW = Math.min(K_PDF_SIG_MAX_W, boxW);
      const maxH = Math.min(K_PDF_SIG_MAX_H, boxH);
      const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
      pendingSignaturePromises.push(
        new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            try {
              const imgW = img.naturalWidth || img.width || 1;
              const imgH = img.naturalHeight || img.height || 1;
              const ratio = Math.min(maxW / imgW, maxH / imgH);
              const drawW = Math.max(1, imgW * ratio);
              const drawH = Math.max(1, imgH * ratio);
              const x = data.cell.x + pad + (boxW - drawW) / 2;
              const y = data.cell.y + pad + (boxH - drawH) / 2;
              doc.setPage(pageNumber);
              doc.addImage(src, getPdfImageType(src, "PNG"), x, y, drawW, drawH, undefined, "NONE");
            } catch {
              /* ignore bad image data */
            }
            resolve();
          };
          img.onerror = () => resolve();
          img.src = src;
        })
      );
    };

    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseDateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };

    let yPosition = await addLogoAndFormHeader(doc, "Risk Assessment Template");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text(`Document Classification: ${raw.documentClassification || "Public"}`, contentX, yPosition);
    yPosition += 6;

    const pdfGrid = {
      font: "helvetica",
      fontSize: 8,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_LIGHT_RGB, fontStyle: "bold" };
    const pdfVal = { fillColor: PDF_WHITE };
    const pdfHdr = { fillColor: THEME_LIGHT_RGB, fontStyle: "bold", fontSize: 8 };
    const pdfBodyRow = { minCellHeight: K_PDF_ASSESSOR_ROW_H, valign: "middle" };
    const pdfSigPlaceholder = {
      content: " ",
      styles: { fillColor: PDF_WHITE, minCellHeight: K_PDF_ASSESSOR_ROW_H, valign: "middle" },
    };
    const pdfRegHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 6,
      halign: "center",
      valign: "middle",
    };
    const pdfNumHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 6,
      halign: "center",
      valign: "middle",
    };
    const pdfRegBody = { fillColor: PDF_WHITE, fontSize: 6.5, valign: "middle", minCellHeight: 10 };

    const ensureSpace = (needed = 20) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = 14;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 20) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    addPdfTable(
      {
        body: [
          [
            { content: "Site:", styles: pdfLbl },
            { content: raw.site || "", styles: pdfVal },
            { content: "Activity", styles: pdfLbl },
            { content: raw.activity || "", styles: { ...pdfVal, minCellHeight: 22, valign: "top" } },
            {
              content: "Persons conducting assessment",
              colSpan: 3,
              styles: { ...pdfLbl, halign: "center", fontSize: 7.5 },
            },
          ],
          [
            { content: "SAP No:", styles: pdfLbl },
            { content: raw.sapNo || "", styles: pdfVal },
            { content: "Risk Assessment No.", styles: pdfLbl },
            { content: raw.riskAssessmentNo || "", styles: pdfVal },
            { content: "Name", styles: { ...pdfLbl, halign: "center" } },
            { content: "Signature", styles: { ...pdfLbl, halign: "center" } },
            { content: "Date", styles: { ...pdfLbl, halign: "center" } },
          ],
          [
            { content: "", colSpan: 2, styles: { ...pdfVal, ...pdfBodyRow } },
            { content: "Review Date", styles: pdfLbl },
            { content: safePdfDate(raw.reviewDate), styles: { ...pdfVal, ...pdfBodyRow } },
            { content: assessors[0]?.name || "", styles: { ...pdfVal, ...pdfBodyRow } },
            isSigImage(assessors[0]?.signature) ? pdfSigPlaceholder : { content: "", styles: { ...pdfVal, ...pdfBodyRow } },
            { content: safePdfDate(assessors[0]?.date), styles: { ...pdfVal, ...pdfBodyRow, halign: "center" } },
          ],
          [
            { content: "", colSpan: 2, styles: { ...pdfVal, ...pdfBodyRow } },
            { content: "Revision No.", styles: pdfLbl },
            { content: raw.revisionNo || "", styles: { ...pdfVal, ...pdfBodyRow } },
            { content: assessors[1]?.name || "", styles: { ...pdfVal, ...pdfBodyRow } },
            isSigImage(assessors[1]?.signature) ? pdfSigPlaceholder : { content: "", styles: { ...pdfVal, ...pdfBodyRow } },
            { content: safePdfDate(assessors[1]?.date), styles: { ...pdfVal, ...pdfBodyRow, halign: "center" } },
          ],
          [
            { content: "Person Supervising Work", styles: pdfLbl },
            { content: raw.personSupervisingWork || "", styles: { ...pdfVal, ...pdfBodyRow } },
            { content: "Employer", styles: pdfLbl },
            { content: raw.employer || "", styles: { ...pdfVal, ...pdfBodyRow } },
            { content: supervisor.name || "", styles: { ...pdfVal, ...pdfBodyRow } },
            isSigImage(supervisor.signature) ? pdfSigPlaceholder : { content: "", styles: { ...pdfVal, ...pdfBodyRow } },
            { content: safePdfDate(supervisor.date), styles: { ...pdfVal, ...pdfBodyRow, halign: "center" } },
          ],
        ],
        columnStyles: {
          0: { cellWidth: contentW * 0.11 },
          1: { cellWidth: contentW * 0.15 },
          2: { cellWidth: contentW * 0.11 },
          3: { cellWidth: contentW * 0.15 },
          4: { cellWidth: contentW * 0.16 },
          5: { cellWidth: contentW * 0.14 },
          6: { cellWidth: contentW * 0.18 },
        },
        styles: pdfGrid,
        didDrawCell: (data) => {
          if (data.section !== "body" || data.column.index !== 5) return;
          if (data.row.index === 2) queueKPdfSignature(assessors[0]?.signature, data);
          else if (data.row.index === 3) queueKPdfSignature(assessors[1]?.signature, data);
          else if (data.row.index === 4) queueKPdfSignature(supervisor.signature, data);
        },
      },
      42
    );

    addPdfTable({
      body: [
        [
          { content: "Persons Exposed", styles: pdfLbl },
          { content: "Employees", styles: pdfLbl },
          { content: pe.employees || "", styles: pdfVal },
          { content: "Other Workers", styles: pdfLbl },
          { content: pe.otherWorkers || "", styles: pdfVal },
          { content: "Public/Visitors", styles: pdfLbl },
          { content: pe.publicVisitors || "", styles: pdfVal },
          { content: "Vulnerable Persons", styles: pdfLbl },
          { content: pe.vulnerablePersons || "", styles: pdfVal },
          { content: "Total at Risk", styles: pdfLbl },
          { content: pe.totalAtRisk || "", styles: pdfVal },
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.1 },
        1: { cellWidth: contentW * 0.09 },
        2: { cellWidth: contentW * 0.08 },
        3: { cellWidth: contentW * 0.1 },
        4: { cellWidth: contentW * 0.08 },
        5: { cellWidth: contentW * 0.1 },
        6: { cellWidth: contentW * 0.08 },
        7: { cellWidth: contentW * 0.12 },
        8: { cellWidth: contentW * 0.08 },
        9: { cellWidth: contentW * 0.09 },
        10: { cellWidth: contentW * 0.08 },
      },
      styles: pdfGrid,
    }, 14);

    const matrixStartY = yPosition;
    const matrixTableW = contentW * 0.36;
    const matrixBody = RISK_MATRIX.map((r) => [
      { content: String(r.severity), styles: { ...pdfLbl, halign: "center" } },
      ...r.cells.map((c) => ({
        content: String(c.score),
        styles: {
          fillColor: riskCellColor(c.score) === "#c6efce" ? [198, 239, 206] : riskCellColor(c.score) === "#ffeb9c" ? [255, 235, 156] : [255, 199, 206],
          halign: "center",
          fontStyle: "bold",
          fontSize: 7,
          valign: "middle",
        },
      })),
    ]);

    autoTable(doc, {
      startY: matrixStartY,
      theme: "grid",
      tableWidth: matrixTableW,
      margin: { left: contentX, right: pageWidth - contentX - matrixTableW },
      head: [
        [
          { content: "Severity", styles: pdfLbl },
          { content: "Likelihood →", colSpan: 5, styles: { ...pdfLbl, halign: "center" } },
        ],
        [
          { content: "", styles: { fillColor: THEME_LIGHT_RGB } },
          ...[1, 2, 3, 4, 5].map((n) => ({ content: String(n), styles: { ...pdfLbl, halign: "center" } })),
        ],
      ],
      body: matrixBody,
      styles: { ...pdfGrid, fontSize: 7 },
      columnStyles: {
        0: { cellWidth: matrixTableW * 0.14 },
        1: { cellWidth: matrixTableW * 0.172 },
        2: { cellWidth: matrixTableW * 0.172 },
        3: { cellWidth: matrixTableW * 0.172 },
        4: { cellWidth: matrixTableW * 0.172 },
        5: { cellWidth: matrixTableW * 0.172 },
      },
    });
    const matrixEndY = doc.lastAutoTable.finalY;

    const legendX = contentX + matrixTableW + 4;
    const legendW = contentW - matrixTableW - 4;
    autoTable(doc, {
      startY: matrixStartY,
      theme: "grid",
      tableWidth: legendW,
      margin: { left: legendX, right: contentX },
      body: [
        [{ content: "Risk = Likelihood x Severity", colSpan: 2, styles: { ...pdfHdr, halign: "center" } }],
        [
          { content: LIKELIHOOD_LEGEND.join("\n"), styles: { fontSize: 6, valign: "top", cellPadding: 2 } },
          { content: SEVERITY_LEGEND.join("\n"), styles: { fontSize: 6, valign: "top", cellPadding: 2 } },
        ],
        [{ content: " ", colSpan: 2, styles: { minCellHeight: 10, fillColor: PDF_WHITE } }],
      ],
      styles: pdfGrid,
      columnStyles: {
        0: { cellWidth: legendW * 0.5 },
        1: { cellWidth: legendW * 0.5 },
      },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.row.index !== 2) return;
        const boxY = data.cell.y + 2;
        const boxH = Math.max(6, data.cell.height - 4);
        const items = [
          { label: "Acceptable", color: [198, 239, 206] },
          { label: "Further review", color: [255, 235, 156] },
          { label: "Unacceptable Risk", color: [255, 199, 206] },
        ];
        const gap = 2;
        const boxW = (data.cell.width - gap * 4) / 3;
        items.forEach((item, i) => {
          const x = data.cell.x + gap + i * (boxW + gap);
          doc.setFillColor(...item.color);
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.2);
          doc.rect(x, boxY, boxW, boxH, "FD");
          doc.setFontSize(5.5);
          doc.setTextColor(0, 0, 0);
          doc.text(item.label, x + boxW / 2, boxY + boxH / 2 + 1.5, { align: "center" });
        });
      },
    });
    yPosition = Math.max(matrixEndY, doc.lastAutoTable.finalY) + 3;

    const hazardBody = hazards.map((row) => [
      row.hazard || "",
      row.initialLikelihood || "",
      row.initialSeverity || "",
      row.initialRisk || "",
      row.controlMeasures || "",
      row.residualLikelihood || "",
      row.residualSeverity || "",
      row.residualRisk || "",
      row.implementedBy || "",
      row.controlFrequencyCheckRate || "",
    ]);
    while (hazardBody.length < HAZARD_ROW_COUNT) {
      hazardBody.push(["", "", "", "", "", "", "", "", "", ""]);
    }

    const hazardHead = [
      [
        { content: "HAZARD", rowSpan: 3, styles: pdfRegHead },
        { content: "1", styles: pdfNumHead },
        { content: "2", styles: pdfNumHead },
        { content: "3", styles: pdfNumHead },
        { content: "4", styles: pdfNumHead },
        { content: "5", styles: pdfNumHead },
        { content: "6", styles: pdfNumHead },
        { content: "7", styles: pdfNumHead },
        { content: "8", styles: pdfNumHead },
        { content: "9", styles: pdfNumHead },
      ],
      [
        { content: "Factors of Harm", colSpan: 2, styles: pdfRegHead },
        { content: "Risk\nMultiple of columns 1 x 2", rowSpan: 2, styles: pdfRegHead },
        { content: "CONTROL MEASURES", rowSpan: 2, styles: pdfRegHead },
        { content: "Factors of Harm", colSpan: 2, styles: pdfRegHead },
        { content: "Residual risk\nMultiple of columns 5 x 6", rowSpan: 2, styles: pdfRegHead },
        { content: "Control measures implemented by (name)", rowSpan: 2, styles: pdfRegHead },
        { content: "Control Frequency Check Rate", rowSpan: 2, styles: pdfRegHead },
      ],
      [
        { content: "Likelihood", styles: pdfRegHead },
        { content: "Severity", styles: pdfRegHead },
        { content: "Likelihood", styles: pdfRegHead },
        { content: "Severity", styles: pdfRegHead },
      ],
    ];

    const hazardColStyles = {
      0: { cellWidth: contentW * 0.15 },
      1: { cellWidth: contentW * 0.045 },
      2: { cellWidth: contentW * 0.045 },
      3: { cellWidth: contentW * 0.05 },
      4: { cellWidth: contentW * 0.16 },
      5: { cellWidth: contentW * 0.045 },
      6: { cellWidth: contentW * 0.045 },
      7: { cellWidth: contentW * 0.05 },
      8: { cellWidth: contentW * 0.11 },
      9: { cellWidth: contentW * 0.11 },
    };

    ensureSpace(40);
    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: pdfMargin,
      rowPageBreak: "auto",
      showHead: "everyPage",
      head: hazardHead,
      body: hazardBody,
      styles: pdfGrid,
      bodyStyles: pdfRegBody,
      headStyles: pdfRegHead,
      columnStyles: hazardColStyles,
    });
    yPosition = doc.lastAutoTable.finalY;

    addPdfTable({
      body: [
        [
          { content: "Hierarchy of Risk Reduction", styles: pdfHdr },
          { content: "Factors to consider in Risk Assessments.", styles: pdfHdr },
        ],
        [
          { content: HIERARCHY_ITEMS.join("\n"), styles: { fontSize: 6.5, valign: "top", cellPadding: 3 } },
          { content: FACTORS_ITEMS.join("\n"), styles: { fontSize: 6.5, valign: "top", cellPadding: 3 } },
        ],
        [{ content: "Continue on page 2", colSpan: 2, styles: { fontSize: 7, fontStyle: "italic", halign: "left" } }],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.5 },
        1: { cellWidth: contentW * 0.5 },
      },
      styles: pdfGrid,
    }, 22);

    applyFooterAllPages(doc);
    await Promise.all(pendingSignaturePromises);

    const displayName = raw.site || record.name || "Template";
    const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date || raw.reviewDate) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("K", getSf009SharePointFields("K", raw, record, getUserName))
        : `SF009K_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.site || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-K Risk Assessment Template PDF generated successfully!");
    }
  };

  const ratingSelect = (value, onChange) => (
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} style={{ ...sf009CellInput, minWidth: 36 }}>
      <option value="">—</option>
      {RATING_OPTIONS.map((n) => (
        <option key={n} value={n}>
          {n}
        </option>
      ))}
    </select>
  );

  const renderTemplateForm = () => {
    const assessors = assessorsForEdit(getPersonsConductingAssessment(fd));
    const supervisor = normalizeSupervisorDetails(fd);
    const pe = fd.personsExposed || DEFAULT_FORM_DATA.personsExposed;
    const hazards = hazardsForPdf(fd.hazardEntries);

    return (
      <div style={{ border: sf009TblBorder, background: "#fff", boxShadow: "0 2px 10px rgba(15,23,42,0.06)" }}>
        <div style={{ padding: "6px 12px", fontSize: 10, color: "#64748b", borderBottom: sf009TblBorder }}>
          Document Classification: {fd.documentClassification || "Public"}
        </div>
        <div style={{ borderBottom: sf009TblBorder }}>
          <div style={{ height: 12, background: "linear-gradient(90deg, #e9b8c8 0%, #ffffff 65%)" }} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px 12px",
            }}
          >
            <div style={{ textAlign: "center", fontWeight: 500, fontSize: 34, color: "#555", lineHeight: 1.05 }}>
              Risk Assessment Template
            </div>
            <img src={onsiteproLogo} alt="Taylor Technology" style={{ height: 44, objectFit: "contain" }} />
          </div>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "12%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "14%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td style={sf009LblCell}>Site:</td>
              <td style={sf009ValCell}>
                <input value={fd.site || ""} onChange={(e) => updateFormData("_flat", "site", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Activity:</td>
              <td style={sf009ValCell}>
                <input value={fd.activity || ""} onChange={(e) => updateFormData("_flat", "activity", e.target.value)} style={sf009CellInput} />
              </td>
              <td colSpan={3} style={{ ...sf009LblCell, textAlign: "center" }}>
                Persons conducting assessment
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>SAP No:</td>
              <td style={sf009ValCell}>
                <input value={fd.sapNo || ""} onChange={(e) => updateFormData("_flat", "sapNo", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Risk Assessment No.</td>
              <td style={sf009ValCell}>
                <input value={fd.riskAssessmentNo || ""} onChange={(e) => updateFormData("_flat", "riskAssessmentNo", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Name</td>
              <td style={sf009LblCell}>Signature</td>
              <td style={sf009LblCell}>Date</td>
            </tr>
            <tr>
              <td colSpan={2} style={sf009ValCell} />
              <td style={sf009LblCell}>Review Date</td>
              <td style={sf009ValCell}>
                <DatePicker selected={safeDate(fd.reviewDate)} onChange={(d) => updateFormData("_flat", "reviewDate", d ? dateToLocalYMD(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0 p-0" wrapperClassName="w-100" />
              </td>
              <td style={sf009ValCell}>
                <input value={assessors[0]?.name || ""} onChange={(e) => updateAssessorRow(0, "name", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={{ ...sf009ValCell, textAlign: "center", padding: 4, minWidth: 120 }}>
                <AssessorSignatureField
                  fieldKey={`${editForm?._id || "new"}-a0-${assessors[0]?.signature?.length || 0}`}
                  signature={assessorSignatureLock[0] || assessors[0]?.signature}
                  sigRef={assessorSigRefs[0]}
                  onSignatureChange={(sig) => updateAssessorRow(0, "signature", sig)}
                />
              </td>
              <td style={sf009ValCell}>
                <DatePicker selected={safeDate(assessors[0]?.date)} onChange={(d) => updateAssessorRow(0, "date", d ? dateToLocalYMD(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0 p-0" wrapperClassName="w-100" />
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={sf009ValCell} />
              <td style={sf009LblCell}>Revision No.</td>
              <td style={sf009ValCell}>
                <input value={fd.revisionNo || ""} onChange={(e) => updateFormData("_flat", "revisionNo", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009ValCell}>
                <input value={assessors[1]?.name || ""} onChange={(e) => updateAssessorRow(1, "name", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={{ ...sf009ValCell, textAlign: "center", padding: 4, minWidth: 120 }}>
                <AssessorSignatureField
                  fieldKey={`${editForm?._id || "new"}-a1-${assessors[1]?.signature?.length || 0}`}
                  signature={assessorSignatureLock[1] || assessors[1]?.signature}
                  sigRef={assessorSigRefs[1]}
                  onSignatureChange={(sig) => updateAssessorRow(1, "signature", sig)}
                />
              </td>
              <td style={sf009ValCell}>
                <DatePicker selected={safeDate(assessors[1]?.date)} onChange={(d) => updateAssessorRow(1, "date", d ? dateToLocalYMD(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0 p-0" wrapperClassName="w-100" />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Person Supervising Work:</td>
              <td style={sf009ValCell}>
                <input value={fd.personSupervisingWork || ""} onChange={(e) => updateFormData("_flat", "personSupervisingWork", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Employer</td>
              <td style={sf009ValCell}>
                <input value={fd.employer || ""} onChange={(e) => updateFormData("_flat", "employer", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009ValCell}>
                <input value={supervisor.name || ""} onChange={(e) => updateSupervisorDetails("name", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={{ ...sf009ValCell, textAlign: "center", padding: 4, minWidth: 120 }}>
                <AssessorSignatureField
                  fieldKey={`${editForm?._id || "new"}-sup-${supervisorSignatureLock?.length || supervisor.signature?.length || 0}`}
                  signature={supervisorSignatureLock || supervisor.signature}
                  sigRef={supervisorSigRef}
                  onSignatureChange={(sig) => updateSupervisorDetails("signature", sig)}
                />
              </td>
              <td style={sf009ValCell}>
                <DatePicker selected={safeDate(supervisor.date)} onChange={(d) => updateSupervisorDetails("date", d ? dateToLocalYMD(d) : "")} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0 p-0" wrapperClassName="w-100" />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Persons Exposed:</td>
              <td style={sf009LblCell}>Employees:</td>
              <td style={sf009ValCell}>
                <input value={pe.employees || ""} onChange={(e) => updateFormData("personsExposed", "employees", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Other Workers:</td>
              <td style={sf009ValCell}>
                <input value={pe.otherWorkers || ""} onChange={(e) => updateFormData("personsExposed", "otherWorkers", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Public/Visitors:</td>
              <td style={sf009ValCell}>
                <input value={pe.publicVisitors || ""} onChange={(e) => updateFormData("personsExposed", "publicVisitors", e.target.value)} style={sf009CellInput} />
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={sf009ValCell} />
              <td style={sf009LblCell}>Vulnerable Persons:</td>
              <td style={sf009ValCell}>
                <input value={pe.vulnerablePersons || ""} onChange={(e) => updateFormData("personsExposed", "vulnerablePersons", e.target.value)} style={sf009CellInput} />
              </td>
              <td style={sf009LblCell}>Total at Risk:</td>
              <td colSpan={2} style={sf009ValCell}>
                <input value={pe.totalAtRisk || ""} onChange={(e) => updateFormData("personsExposed", "totalAtRisk", e.target.value)} style={sf009CellInput} />
              </td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: 10, borderTop: sf009TblBorder }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 10, marginBottom: 4 }}>Risk = Likelihood x Severity</div>
            <table style={{ borderCollapse: "collapse", fontSize: 9 }}>
              <thead>
                <tr>
                  <td style={{ ...sf009LblCell, padding: 4 }}>Sev↓</td>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <td key={n} style={{ ...sf009LblCell, padding: 4, textAlign: "center", minWidth: 28 }}>
                      {n}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RISK_MATRIX.map((r) => (
                  <tr key={r.severity}>
                    <td style={{ ...sf009LblCell, padding: 4, textAlign: "center" }}>{r.severity}</td>
                    {r.cells.map((c) => (
                      <td
                        key={c.likelihood}
                        style={{
                          border: sf009TblBorder,
                          textAlign: "center",
                          fontWeight: 600,
                          background: riskCellColor(c.score),
                          padding: 4,
                        }}
                      >
                        {c.score}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ flex: 1, minWidth: 140, fontSize: 9, lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Likelihood</div>
            {LIKELIHOOD_LEGEND.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 140, fontSize: 9, lineHeight: 1.45 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Severity</div>
            {SEVERITY_LEGEND.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
            <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 58, height: 10, display: "inline-block", background: "#00ff00", border: "1px solid #7f1d1d22" }} />
                <span>Acceptable</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 58, height: 10, display: "inline-block", background: "#ffff00", border: "1px solid #7f1d1d22" }} />
                <span>Further review</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 58, height: 10, display: "inline-block", background: "#ff0000", border: "1px solid #7f1d1d22" }} />
                <span>Unacceptable Risk</span>
              </div>
            </div>
          </div>
        </div>

        <div style={{ overflowX: "auto", borderTop: sf009TblBorder }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1120 }}>
            <thead>
              <tr>
                <th rowSpan={2} style={sf009RegHeadCell}>HAZARD</th>
                <th colSpan={2} style={sf009RegHeadCell}>Factors of Harm</th>
                <th rowSpan={2} style={sf009RegHeadCell}>3 Risk Multiple of columns 1 x 2</th>
                <th rowSpan={2} style={sf009RegHeadCell}>4 CONTROL MEASURES</th>
                <th colSpan={2} style={sf009RegHeadCell}>Factors of Harm</th>
                <th rowSpan={2} style={sf009RegHeadCell}>7 Residual risk Multiple of columns 5 x 6</th>
                <th rowSpan={2} style={sf009RegHeadCell}>8 Control measures implemented by (name)</th>
                <th rowSpan={2} style={sf009RegHeadCell}>9 Control Frequency Check Rate</th>
              </tr>
              <tr>
                <th style={sf009RegHeadCell}>1 Likelihood</th>
                <th style={sf009RegHeadCell}>2 Severity</th>
                <th style={sf009RegHeadCell}>5 Likelihood</th>
                <th style={sf009RegHeadCell}>6 Severity</th>
              </tr>
            </thead>
            <tbody>
              {hazards.map((row, idx) => (
                <tr key={idx}>
                  <td style={sf009RegCell}>
                    <input value={row.hazard || ""} onChange={(e) => updateHazardRow(idx, "hazard", e.target.value)} style={sf009CellInput} />
                  </td>
                  <td style={sf009RegCell}>{ratingSelect(row.initialLikelihood, (v) => updateHazardRow(idx, "initialLikelihood", v))}</td>
                  <td style={sf009RegCell}>{ratingSelect(row.initialSeverity, (v) => updateHazardRow(idx, "initialSeverity", v))}</td>
                  <td style={{ ...sf009RegCell, background: riskCellColor(row.initialRisk), textAlign: "center", fontWeight: 600 }}>
                    {row.initialRisk || "—"}
                  </td>
                  <td style={sf009RegCell}>
                    <input value={row.controlMeasures || ""} onChange={(e) => updateHazardRow(idx, "controlMeasures", e.target.value)} style={sf009CellInput} />
                  </td>
                  <td style={sf009RegCell}>{ratingSelect(row.residualLikelihood, (v) => updateHazardRow(idx, "residualLikelihood", v))}</td>
                  <td style={sf009RegCell}>{ratingSelect(row.residualSeverity, (v) => updateHazardRow(idx, "residualSeverity", v))}</td>
                  <td style={{ ...sf009RegCell, background: riskCellColor(row.residualRisk), textAlign: "center", fontWeight: 600 }}>
                    {row.residualRisk || "—"}
                  </td>
                  <td style={sf009RegCell}>
                    <input value={row.implementedBy || ""} onChange={(e) => updateHazardRow(idx, "implementedBy", e.target.value)} style={sf009CellInput} />
                  </td>
                  <td style={sf009RegCell}>
                    <input value={row.controlFrequencyCheckRate || ""} onChange={(e) => updateHazardRow(idx, "controlFrequencyCheckRate", e.target.value)} style={sf009CellInput} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: sf009TblBorder }}>
          <tbody>
            <tr>
              <td style={{ ...sf009StaticCell, width: "50%" }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Hierarchy of Risk Reduction</div>
                {HIERARCHY_ITEMS.map((item, i) => (
                  <div key={i}>{item}</div>
                ))}
              </td>
              <td style={sf009StaticCell}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Factors to consider in Risk Assessments:</div>
                {FACTORS_ITEMS.map((item, i) => (
                  <div key={i}>{item}</div>
                ))}
              </td>
            </tr>
            <tr>
              <td colSpan={2} style={{ ...sf009StaticCell, fontStyle: "italic" }}>
                Continue on page 2
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-K Risk Assessment Template</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button type="button" onClick={() => setShowImportModal(true)} style={{ background: THEME, color: "#fff", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none" }} />
              <input type="text" placeholder="Type to Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }} />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Activity</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Risk Assessment No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>SAP No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096" }}>No records found.</td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: 16, fontSize: 14 }}>{raw.site || "—"}</td>
                      <td style={{ padding: 16, fontSize: 14 }}>{raw.activity || "—"}</td>
                      <td style={{ padding: 16, fontSize: 14 }}>{raw.riskAssessmentNo || "—"}</td>
                      <td style={{ padding: 16, fontSize: 14 }}>{raw.sapNo || "—"}</td>
                      <td style={{ padding: 16, fontSize: 14 }}>{getUserName(r)}</td>
                      <td style={{ padding: 16, fontSize: 14 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.site || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME }}>{page} / {totalPages}</span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-009-K Risk Assessment Template</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => { setShowEditModal(false); setAssessorSignatureLock([]); }} />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>{renderTemplateForm()}</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowEditModal(false); setAssessorSignatureLock([]); }}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal open={showImportModal} onClose={() => setShowImportModal(false)} title="Upload SF-009-K Risk Assessment Template File" importPath="sf-009-k-risk-assessment-template/import-form" isAdminUser={isAdminUser} onImported={({ insertedRow }) => { if (insertedRow?._id) { setForms((prev) => [insertedRow, ...prev]); setPage(1); } }} />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-K Risk Assessment Template</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009KRiskAssessmentTemplatePage;